import { arxAbi } from '@/config/abi/arx.abi'
import { chain } from '@/config/chain'
import { useErc20BalanceOf } from '@/hooks/useErc20BalanceOf'
import { API_BASE_URL } from '@/services/api'
import { formatEthDisplay, formatWithCommas } from '@/utils/format'
import {
  checkSufficientBalance,
  computeRequiredFeeWithMargin,
  formatFeeDisplay,
  isBalanceTightForArxFee,
} from '@/utils/gasEstimation'
import { Icon } from '@components/icons/Icon'
import { MainLayout } from '@components/layouts/MainLayout'
import { useWallet } from '@components/providers/WalletProvider'
import { TokenSelectSheet, type TokenType } from '@components/transfer/TokenSelectSheet'
import {
  TransferModal,
  type TransferModalStep,
  type TransferStatus,
} from '@components/transfer/TransferModal'
import { ArcsTokenIcon } from '@components/ui/ArcsTokenIcon'
import { IconButton } from '@components/ui/IconButton'
import { useSmartWallets } from '@privy-io/react-auth/smart-wallets'
import { fetchAuthSession } from 'aws-amplify/auth'
import type React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { type Address, encodeFunctionData, isAddress, parseAbi, parseUnits } from 'viem'
import { useBalance } from 'wagmi'
import ethereumTokenIcon from '../../public/icons/EthereumTokenIcon.svg'

// カンマ付きの文字列をカンマ無しの数値文字列に変換
const parseFromCommas = (value: string): string => {
  return value.replace(/,/g, '')
}

/** pm_getPaymasterStubData 用の仮 UserOp。API はこれから totalFee を計算する。実際の送金は本物の UserOp + feeMarginPercent で行う。maxFeePerGas は高め(100 gwei)にし、API 側で現在ガス価格が使われるようにする。 */
const STUB_USER_OP_GAS = {
  callGasLimit: '0x186a0',
  verificationGasLimit: '0x7A120',
  preVerificationGas: '0x5208',
  maxFeePerGas: '0x174876e800', // 100 gwei
  maxPriorityFeePerGas: '0x174876e800',
} as const

const Transfer: React.FC = () => {
  const navigate = useNavigate()
  const { smartWalletAddress, isSmartWalletReady } = useWallet()
  const { client } = useSmartWallets()

  // 入力フォームの状態
  const [recipientAddress, setRecipientAddress] = useState('')
  const [amount, setAmount] = useState('')
  const [sendToken, setSendToken] = useState<TokenType>('arcs')
  const [feeToken, setFeeToken] = useState<TokenType>('arcs')

  // トークン選択シートの開閉状態
  const [isSendTokenSheetOpen, setIsSendTokenSheetOpen] = useState(false)
  const [isFeeTokenSheetOpen, setIsFeeTokenSheetOpen] = useState(false)

  // 送金モーダルの状態
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalStep, setModalStep] = useState<TransferModalStep>('confirm')
  const [transferStatus, setTransferStatus] = useState<TransferStatus>('idle')
  const [error, setError] = useState<string | null>(null)

  // 手数料推定の状態（唯一の情報源: wei 単位の BigInt）
  const [estimatedFeeWei, setEstimatedFeeWei] = useState<bigint | null>(null)
  const [isEstimatingFee, setIsEstimatingFee] = useState(false)

  // コントラクトアドレス（環境変数から取得）
  const arcsTokenAddress = import.meta.env.VITE_ARCS_TOKEN_ADDRESS as Address
  const paymasterAddress = import.meta.env.VITE_PAYMASTER_ADDRESS as Address
  const paymasterConstantFeeWei = BigInt(import.meta.env.VITE_PAYMASTER_CONSTANT_FEE_WEI ?? '0')
  const feeMarginPercent = (() => {
    const raw = import.meta.env.VITE_PAYMASTER_FEE_MARGIN_PERCENT
    const n = raw != null ? Number.parseInt(raw, 10) : 50
    return Number.isNaN(n) || n < 0 ? 50 : n
  })()

  // ETH残高（送金ページではマウント時に必ずリフェッチ）
  const { data: ethBalance, refetch: refetchEthBalance } = useBalance({
    address: smartWalletAddress as Address,
    chainId: chain.id,
    query: {
      staleTime: 0,
      refetchOnMount: 'always' as const,
    },
  })

  // ARCS残高（同上）
  const { data: arcsBalance, refetch: refetchArcsBalance } = useErc20BalanceOf(
    arcsTokenAddress,
    smartWalletAddress as Address,
    { staleTime: 0, refetchOnMount: 'always' }
  )

  // 残高を表示用の文字列に整形
  const formatBalance = (balance: bigint | undefined, decimals = 18) => {
    if (!balance) return '0'
    return formatEthDisplay(Number(balance) / 10 ** decimals)
  }

  // 現在選択中トークンの残高（表示用）
  const currentBalance =
    sendToken === 'arcs'
      ? formatBalance(arcsBalance as bigint | undefined, 18)
      : formatBalance(ethBalance?.value)

  const currentSymbol = sendToken === 'arcs' ? 'ARX' : 'ETH'

  // 手数料の推定（estimatedFeeWei のみを更新 — 表示・バリデーション・送金額はここから導出）
  useEffect(() => {
    let cancelled = false
    setEstimatedFeeWei(null)

    const estimateFee = async () => {
      if (!smartWalletAddress || !client || !amount || Number(amount) <= 0 || !recipientAddress) {
        if (cancelled) return
        setEstimatedFeeWei(null)
        return
      }

      if (cancelled) return
      setIsEstimatingFee(true)

      try {
        const paymasterApiUrl = `${API_BASE_URL}/paymaster/rpc`
        const stubUserOp = {
          sender: smartWalletAddress as Address,
          nonce: '0x0',
          callData: '0x',
          ...STUB_USER_OP_GAS,
        }
        const session = await fetchAuthSession()
        if (cancelled) return
        const authToken = session.tokens?.idToken?.toString()

        if (feeToken === 'arcs') {
          const response = await fetch(paymasterApiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              jsonrpc: '2.0',
              id: 1,
              method: 'pm_getPaymasterStubData',
              params: [
                stubUserOp,
                '0x0000000071727De22E5E9d8BAf0edAc6f37da032',
                `0x${chain.id.toString(16)}`,
                { token: arcsTokenAddress, authToken },
              ],
            }),
          })

          const data = await response.json()
          if (cancelled) return
          const totalFeeTokenWeiHex = data.result?.context?.totalFeeTokenWei as string | undefined

          if (totalFeeTokenWeiHex) {
            const cleanHex = totalFeeTokenWeiHex.startsWith('0x')
              ? totalFeeTokenWeiHex
              : `0x${totalFeeTokenWeiHex}`
            if (!cancelled) setEstimatedFeeWei(BigInt(cleanHex))
          } else {
            if (!cancelled) setEstimatedFeeWei(null)
          }
        } else {
          const response = await fetch(paymasterApiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              jsonrpc: '2.0',
              id: 1,
              method: 'pm_getPaymasterStubData',
              params: [
                stubUserOp,
                '0x0000000071727De22E5E9d8BAf0edAc6f37da032',
                `0x${chain.id.toString(16)}`,
                { payWith: 'eth', authToken },
              ],
            }),
          })
          const data = await response.json()
          if (cancelled) return
          const totalFeeEthWeiHex = data.result?.context?.totalFeeEthWei as string | undefined

          if (totalFeeEthWeiHex) {
            const cleanHex = totalFeeEthWeiHex.startsWith('0x')
              ? totalFeeEthWeiHex
              : `0x${totalFeeEthWeiHex}`
            if (!cancelled) setEstimatedFeeWei(BigInt(cleanHex))
          } else {
            // API がフォールバックした場合もガス代見積もりを wei で保持する
            const gasUsed = sendToken === 'arcs' ? 65000n : 21000n
            const ethCost = gasUsed * 20000000000n
            if (!cancelled) setEstimatedFeeWei(ethCost)
          }
        }
      } catch (err) {
        console.error('Fee estimation error:', err)
        if (!cancelled) setEstimatedFeeWei(null)
      } finally {
        if (!cancelled) setIsEstimatingFee(false)
      }
    }

    estimateFee()

    return () => {
      cancelled = true
    }
  }, [amount, recipientAddress, feeToken, sendToken, smartWalletAddress, client, arcsTokenAddress])

  // アドレスをクリップボードから貼り付け
  const handlePasteAddress = useCallback(async () => {
    try {
      const text = (await navigator.clipboard.readText()).trim()
      if (isAddress(text)) {
        setRecipientAddress(text)
      }
    } catch (err) {
      console.error('Failed to read clipboard:', err)
    }
  }, [])

  // アドレス入力欄をクリア
  const handleClearAddress = useCallback(() => {
    setRecipientAddress('')
  }, [])

  // 手数料（固定分はそのまま、ガス分にマージン）— 表示・バリデーション・送金すべてで同一値を使う
  const requiredFeeWeiWithMargin = useMemo(
    () =>
      computeRequiredFeeWithMargin({
        estimatedFeeWei,
        feeToken,
        paymasterConstantFeeWei,
        feeMarginPercent,
      }),
    [feeToken, estimatedFeeWei, paymasterConstantFeeWei, feeMarginPercent]
  )

  // 表示用: requiredFeeWeiWithMargin から一意に導出（estimatedFee 文字列は不要）
  const displayFeeText = formatFeeDisplay(requiredFeeWeiWithMargin)

  // 「送金」ボタンクリック時の処理（モーダルを開く）
  const handleTransferClick = useCallback(() => {
    if (!recipientAddress || !amount || Number(amount) <= 0) return
    setModalStep('confirm')
    setTransferStatus('idle')
    setError(null)
    setIsModalOpen(true)
  }, [recipientAddress, amount])

  // 実際の送金処理
  const handleConfirmTransfer = useCallback(async () => {
    if (!smartWalletAddress || !client || !isSmartWalletReady) {
      setError('ウォレットが準備されていません')
      setTransferStatus('error')
      return
    }

    setModalStep('processing')
    setTransferStatus('confirming')
    setError(null)

    try {
      const amountValue = parseUnits(amount, 18)
      // 承認額＝表示手数料（ボタン判定と同じ）
      const paymasterApprovalAmount = feeToken === 'arcs' ? requiredFeeWeiWithMargin : 0n

      if (feeToken === 'arcs') {
        // Use Paymaster for ARCS fee payment
        setTransferStatus('sending')

        const session = await fetchAuthSession()
        const authToken = session.tokens?.idToken?.toString()

        if (sendToken === 'arcs') {
          // ARCS送金 + ARCS手数料（Paymaster経由）
          const transferData = encodeFunctionData({
            abi: arxAbi,
            functionName: 'transfer',
            args: [recipientAddress as Address, amountValue],
          })

          await client.sendTransaction(
            {
              calls: [
                { to: arcsTokenAddress, data: transferData, value: 0n },
                {
                  to: arcsTokenAddress,
                  abi: parseAbi(['function approve(address,uint256)']),
                  functionName: 'approve',
                  args: [paymasterAddress, paymasterApprovalAmount],
                },
              ],
              paymasterContext: {
                token: arcsTokenAddress,
                authToken,
              },
            },
            { uiOptions: { showWalletUIs: false } }
          )
        } else {
          // ETH送金 + ARCS手数料（Paymaster経由）
          await client.sendTransaction(
            {
              calls: [
                { to: recipientAddress as Address, value: amountValue },
                {
                  to: arcsTokenAddress,
                  abi: parseAbi(['function approve(address,uint256)']),
                  functionName: 'approve',
                  args: [paymasterAddress, paymasterApprovalAmount],
                },
              ],
              paymasterContext: {
                token: arcsTokenAddress,
                authToken,
              },
            },
            { uiOptions: { showWalletUIs: false } }
          )
        }
      } else {
        // ETHで手数料を支払う（Paymaster Mode2: calls 先頭で Paymaster へ ETH を送り、postOp で精算）
        setTransferStatus('sending')
        const session = await fetchAuthSession()
        const authToken = session.tokens?.idToken?.toString()
        const ethFeeAmount = requiredFeeWeiWithMargin

        console.log(
          '[Transfer][Mode2] sending with ETH fee',
          JSON.stringify(
            {
              sendToken,
              feeToken,
              amount,
              recipientAddress,
              paymasterAddress,
              estimatedFeeWei: estimatedFeeWei != null ? estimatedFeeWei.toString() : null,
              requiredFeeWeiWithMargin: requiredFeeWeiWithMargin.toString(),
              ethFeeAmount: ethFeeAmount.toString(),
            },
            null,
            2
          )
        )

        if (sendToken === 'arcs') {
          const transferData = encodeFunctionData({
            abi: arxAbi,
            functionName: 'transfer',
            args: [recipientAddress as Address, amountValue],
          })
          await client.sendTransaction(
            {
              calls: [
                { to: paymasterAddress, value: ethFeeAmount },
                { to: arcsTokenAddress, data: transferData, value: 0n },
              ],
              paymasterContext: { payWith: 'eth', authToken },
            },
            { uiOptions: { showWalletUIs: false } }
          )
        } else {
          await client.sendTransaction(
            {
              calls: [
                { to: paymasterAddress, value: ethFeeAmount },
                { to: recipientAddress as Address, value: amountValue },
              ],
              paymasterContext: { payWith: 'eth', authToken },
            },
            { uiOptions: { showWalletUIs: false } }
          )
        }
      }

      setTransferStatus('completed')
      setModalStep('success')

      // 送金完了後に残高を即座にリフェッチ
      refetchEthBalance()
      refetchArcsBalance()
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err)
      const causeMsg =
        err instanceof Error && 'cause' in err && err.cause instanceof Error
          ? String((err.cause as Error).message)
          : ''
      const shortMsg =
        err && typeof err === 'object' && 'shortMessage' in err
          ? String((err as { shortMessage?: string }).shortMessage)
          : ''
      const detail = {
        message: raw,
        cause: causeMsg || undefined,
        shortMessage: shortMsg || undefined,
      }
      console.error('[Transfer] error:', err)
      console.error('[Transfer] error detail (for debugging):', JSON.stringify(detail, null, 2))

      let uiMessage = '送金に失敗しました。しばらく時間をおいてから再度お試しください。'

      const lower = raw.toLowerCase()
      const combined = `${lower} ${causeMsg.toLowerCase()} ${shortMsg.toLowerCase()}`
      if (lower.includes('user rejected') || lower.includes('user denied')) {
        uiMessage = 'ウォレットで送金がキャンセルされました。'
      } else if (raw.includes('Authentication required') || raw.includes('JWT')) {
        uiMessage = '認証に失敗しました。再ログインしてからお試しください。'
      } else if (raw.includes('insufficient funds')) {
        uiMessage = '残高が不足しています。残高を確認してから再度お試しください。'
      } else if (
        combined.includes('postop') ||
        combined.includes('0x7939f424') ||
        combined.includes('0xdb49e077') ||
        combined.includes('aa50') ||
        combined.includes('transferfromfailed') ||
        combined.includes('nativeethinsufficient')
      ) {
        uiMessage =
          '手数料支払い時に残高が不足していた可能性があります。残高が十分であることをご確認のうえ、時間をおいて再度お試しください。'
      } else if (combined.includes('cannot build paymasterData when fee is 0')) {
        uiMessage = '手数料の取得に失敗しました。画面を更新して再度お試しください。'
      } else if (
        combined.includes('validuntil') ||
        combined.includes('valid until') ||
        combined.includes('expired') ||
        combined.includes('validity') ||
        combined.includes('期限')
      ) {
        uiMessage = '手数料の見積もりが期限切れです。画面を更新して再度お試しください。'
      }

      setError(uiMessage)
      setTransferStatus('error')
    }
  }, [
    smartWalletAddress,
    client,
    isSmartWalletReady,
    amount,
    sendToken,
    feeToken,
    recipientAddress,
    arcsTokenAddress,
    paymasterAddress,
    requiredFeeWeiWithMargin,
    estimatedFeeWei,
    refetchEthBalance,
    refetchArcsBalance,
  ])

  // モーダルを閉じる処理
  const handleCloseModal = useCallback(() => {
    if (modalStep === 'processing' && transferStatus !== 'error') return
    setIsModalOpen(false)
    if (modalStep === 'success') {
      setAmount('')
      setRecipientAddress('')
    }
  }, [modalStep, transferStatus])

  // 送金ボタン有効条件：残高 >= 送金額 + 手数料（表示している手数料と同一）
  const hasSufficientBalance = useMemo(
    () =>
      checkSufficientBalance({
        amount,
        sendToken,
        feeToken,
        requiredFeeWeiWithMargin,
        arcsBalance: (arcsBalance as bigint | undefined) ?? 0n,
        ethBalance: (ethBalance?.value as bigint | undefined) ?? 0n,
        parseUnits,
      }),
    [amount, sendToken, feeToken, requiredFeeWeiWithMargin, arcsBalance, ethBalance?.value]
  )

  const canTransfer =
    recipientAddress &&
    isAddress(recipientAddress) &&
    amount &&
    Number(amount) > 0 &&
    isSmartWalletReady &&
    hasSufficientBalance

  // ARX手数料かつ残高がギリギリのときのみ「失敗しうる」注意を表示（余裕があれば表示しない）
  const isArxFeeBalanceTight = useMemo(
    () =>
      isBalanceTightForArxFee({
        amount,
        sendToken,
        feeToken,
        requiredFeeWeiWithMargin,
        arcsBalance: (arcsBalance as bigint | undefined) ?? 0n,
        parseUnits,
      }),
    [feeToken, amount, sendToken, requiredFeeWeiWithMargin, arcsBalance]
  )

  // 送金ボタンが無効なときの理由（ユーザーに表示）
  const transferDisabledReason: string | null = !canTransfer
    ? !recipientAddress
      ? '送金先アドレスを入力してください'
      : !isAddress(recipientAddress)
        ? '有効なアドレスを入力してください'
        : !amount || Number(amount) <= 0
          ? '送金額を入力してください'
          : !isSmartWalletReady
            ? 'ウォレットを準備しています…'
            : !hasSufficientBalance
              ? '残高が不足しています（送金額＋手数料をご確認ください）'
              : null
    : null

  return (
    <MainLayout
      activeMain={null}
      header={{ enabled: false }}
      outerClassName="bg-white"
      className="bg-white"
      contentClassName=""
      showBottomNav={false}
    >
      <div className="flex flex-col h-full min-h-screen">
        {/* カスタムヘッダー */}
        <header className="flex items-center justify-between px-4 py-3">
          <IconButton aria-label="閉じる" icon="Close" onClick={() => navigate(-1)} />
          <h1 className="text-xl font-bold text-zinc-900">送金</h1>
          <div className="size-10" aria-hidden />
        </header>

        {/* メインコンテンツ */}
        <div className="flex-1 flex flex-col gap-3 px-4 pt-2">
          {/* トークンセレクター */}
          <button
            type="button"
            onClick={() => setIsSendTokenSheetOpen(true)}
            className="flex items-center justify-between p-3 border border-zinc-200 rounded-2xl bg-white hover:bg-zinc-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              {sendToken === 'arcs' ? (
                <ArcsTokenIcon className="size-10" />
              ) : (
                <img src={ethereumTokenIcon} alt="" aria-hidden="true" className="size-10" />
              )}
              <div className="flex flex-col items-start">
                <span className="text-base font-semibold text-zinc-900">
                  {sendToken === 'arcs' ? 'ARCS' : 'Ethereum'}
                </span>
                <span className="text-sm text-zinc-500">
                  {currentBalance} {currentSymbol}
                </span>
              </div>
            </div>
            <Icon content="ChevronRight" className="size-5 text-zinc-400 rotate-90" />
          </button>

          {/* アドレス入力 */}
          <div className="grid grid-cols-[auto_1fr_auto] border border-zinc-200 rounded-2xl bg-white">
            <div className="flex items-center justify-center px-4 bg-zinc-100 text-sm text-zinc-500 rounded-l-2xl border-r border-zinc-200">
              To
            </div>
            <input
              type="text"
              value={recipientAddress}
              onChange={(e) => setRecipientAddress(e.target.value)}
              placeholder="Address"
              className="min-w-0 px-3 py-3 bg-transparent text-base font-medium text-zinc-900 placeholder:text-zinc-400 outline-none"
            />
            <div className="flex items-center pr-3">
              {recipientAddress ? (
                <button
                  type="button"
                  onClick={handleClearAddress}
                  className="size-6 flex items-center justify-center bg-zinc-200 rounded-full"
                  aria-label="クリア"
                >
                  <svg
                    className="size-3 text-zinc-500"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M18 6L6 18M6 6L18 18"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handlePasteAddress}
                  className="px-3 py-1 bg-blue-400 text-white text-sm font-medium rounded-lg"
                >
                  Paste
                </button>
              )}
            </div>
          </div>

          {/* 金額入力 */}
          <div className="grid grid-cols-[1fr_auto] border border-zinc-200 rounded-2xl bg-white">
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              value={formatWithCommas(amount)}
              onChange={(e) => {
                const raw = parseFromCommas(e.target.value)
                // Allow only numbers and one decimal point
                if (/^\d*\.?\d*$/.test(raw)) {
                  setAmount(raw)
                }
              }}
              placeholder="0"
              className="min-w-0 px-4 py-4 bg-transparent text-2xl font-semibold text-zinc-900 placeholder:text-zinc-300 outline-none"
            />
            <div className="flex items-center justify-center px-4 bg-zinc-100 text-base font-medium text-zinc-400 rounded-r-2xl border-l border-zinc-200">
              {currentSymbol}
            </div>
          </div>

          {/* 手数料トークンセレクター */}
          <button
            type="button"
            onClick={() => setIsFeeTokenSheetOpen(true)}
            className="flex flex-col gap-2 p-3 border border-zinc-200 rounded-2xl bg-white hover:bg-zinc-50 transition-colors text-left w-full"
          >
            {/* 1行目: トークンアイコン、名前、ギアアイコン */}
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                {feeToken === 'arcs' ? (
                  <ArcsTokenIcon className="size-4" />
                ) : (
                  <img src={ethereumTokenIcon} alt="" aria-hidden="true" className="size-4" />
                )}
                <span className="text-sm font-semibold text-zinc-900">
                  {feeToken === 'arcs' ? 'ARCS' : 'Ethereum'}
                </span>
              </div>
              <Icon content="Gear" className="size-5" />
            </div>
            {/* 2行目: 推定手数料ラベル、値と単位 */}
            <div className="flex items-center justify-between w-full">
              <span className="text-xs text-zinc-500">推定手数料</span>
              <span className="text-sm font-medium text-zinc-900">
                {isEstimatingFee
                  ? '計算中...'
                  : `${displayFeeText} ${feeToken === 'arcs' ? 'ARX' : 'ETH'}`}
              </span>
            </div>
          </button>

          {/* 合計セクション（手数料が確定済みで金額がある場合のみ表示） */}
          {!isEstimatingFee && requiredFeeWeiWithMargin > 0n && amount && Number(amount) > 0 && (
            <div className="flex flex-col gap-2 p-3 border border-zinc-200 rounded-2xl bg-zinc-50">
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-500">送金額</span>
                <span className="text-sm font-medium text-zinc-900">
                  {formatWithCommas(amount)} {currentSymbol}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-500">推定手数料</span>
                <span className="text-sm font-medium text-zinc-900">
                  {displayFeeText} {feeToken === 'arcs' ? 'ARX' : 'ETH'}
                </span>
              </div>
              {sendToken === feeToken && (
                <>
                  <div className="border-t border-zinc-200" />
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-zinc-700">合計</span>
                    <span className="text-sm font-semibold text-zinc-900">
                      {formatWithCommas(
                        formatEthDisplay(
                          Number.parseFloat(amount) + Number(requiredFeeWeiWithMargin) / 1e18
                        )
                      )}{' '}
                      {currentSymbol}
                    </span>
                  </div>
                </>
              )}
            </div>
          )}

          {/* 送金ボタン */}
          <div className="mt-auto py-4 pb-8 flex flex-col gap-2">
            {transferDisabledReason && (
              <p className="text-xs text-red-500 text-center">{transferDisabledReason}</p>
            )}
            {feeToken === 'arcs' && isArxFeeBalanceTight && !transferDisabledReason && (
              <p className="text-xs text-red-500 text-center">
                送金額＋手数料に近い残高のため、送金が失敗することがあります。失敗した場合は表示されるエラーをご確認し、残高を確認してから再度お試しください。
              </p>
            )}
            <button
              type="button"
              onClick={handleTransferClick}
              disabled={!canTransfer}
              className={`w-full py-3 px-5 rounded-xl text-base font-semibold transition-colors ${
                canTransfer
                  ? 'bg-blue-500 border border-blue-600 text-white shadow-[0px_0px_0px_1px_#d6f2fe,0px_0px_0px_4px_#f3fbff,0px_1px_1px_0.5px_rgba(24,24,27,0.05)] hover:bg-blue-600'
                  : 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
              }`}
            >
              送金
            </button>
          </div>
        </div>
      </div>

      {/* トークンセレクトシート */}
      <TokenSelectSheet
        open={isSendTokenSheetOpen}
        title="送金トークンの選択"
        description="どのトークンを送金しますか？"
        selectedToken={sendToken}
        onSelect={setSendToken}
        onClose={() => setIsSendTokenSheetOpen(false)}
      />

      <TokenSelectSheet
        open={isFeeTokenSheetOpen}
        title="手数料の設定"
        description="どのトークンで手数料を支払いますか？"
        selectedToken={feeToken}
        onSelect={setFeeToken}
        onClose={() => setIsFeeTokenSheetOpen(false)}
      />

      {/* 送金モーダル */}
      <TransferModal
        isOpen={isModalOpen}
        step={modalStep}
        tokenType={sendToken}
        amount={amount}
        recipientAddress={recipientAddress}
        status={transferStatus}
        error={error}
        onConfirm={handleConfirmTransfer}
        onClose={handleCloseModal}
        feeText={displayFeeText}
        feeSymbol={feeToken === 'arcs' ? 'ARX' : 'ETH'}
        sendToken={sendToken}
        feeToken={feeToken}
      />
    </MainLayout>
  )
}

export default Transfer
